import {
  CONTRACT_KINDS,
  EMPLOYEE_STATUSES,
  formatDate,
  formatPhone,
  fullName,
  type ContractKind,
  type EmployeeStatus,
} from '@hr/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { KeyRoundIcon, PlusIcon, UserRoundIcon } from 'lucide-react';
import { useMemo } from 'react';
import { z } from 'zod';
import { Combobox } from '@/components/combobox';
import { DataTable } from '@/components/data-table';
import { EmptyState, PageHeader } from '@/components/page';
import { SearchInput } from '@/components/search-input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EmployeeCreateSheet } from '@/features/personnel/employee-form';
import { EmployeeStatusBadge } from '@/features/personnel/status';
import { useRegistry } from '@/features/registry/use-registry';
import { CONTRACT_KIND_LABELS, EMPLOYEE_STATUS_LABELS, uk } from '@/i18n/uk';
import { isHr } from '@/lib/roles';
import { listSearchShape, tableState, tableStateToSearch } from '@/lib/search-params';
import { trpc } from '@/lib/trpc';
import type { EmployeeListItem } from '@/lib/types';

const searchSchema = z.object({
  ...listSearchShape,
  status: z.enum(EMPLOYEE_STATUSES).optional().catch(undefined),
  project: z.uuid().optional().catch(undefined),
  center: z.uuid().optional().catch(undefined),
  kind: z.enum(CONTRACT_KINDS).optional().catch(undefined),
  new: z.boolean().optional().catch(undefined),
});

export const Route = createFileRoute('/_app/employees/')({
  validateSearch: searchSchema,
  component: EmployeesPage,
});

function EmployeesPage() {
  const t = uk.personnel;
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { me } = Route.useRouteContext();
  const canWrite = isHr(me.role);
  const registry = useRegistry();
  const state = tableState(search, { sort: 'name', dir: 'asc' });
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  // За замовчуванням — тільки ті, хто працює.
  const status = search.status ?? 'active';

  const list = useQuery({
    ...trpc.employees.list.queryOptions({
      page: state.page,
      pageSize: state.pageSize,
      sort: state.sort,
      dir: state.dir,
      q: search.q,
      status,
      projectId: search.project,
      centerId: search.center,
      contractKind: search.kind,
    }),
    placeholderData: keepPreviousData,
  });

  const columns = useMemo<ColumnDef<EmployeeListItem>[]>(
    () => [
      {
        id: 'name',
        header: t.fullName,
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1.5 font-medium">
            {fullName(row.original)}
            {row.original.hasAccount && (
              <KeyRoundIcon className="size-3.5 text-muted-foreground" aria-label={t.hasAccount} />
            )}
          </span>
        ),
        meta: { className: 'min-w-56' },
      },
      {
        id: 'positions',
        header: t.currentPositions,
        enableSorting: false,
        cell: ({ row }) => row.original.currentPositions ?? uk.common.empty,
        meta: { className: 'min-w-64' },
      },
      {
        id: 'contractKinds',
        header: uk.registry.contractKind,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.contractKinds.length
            ? row.original.contractKinds.map((k) => (
                <div key={k} className="whitespace-nowrap">
                  {CONTRACT_KIND_LABELS[k]}
                </div>
              ))
            : uk.common.empty,
      },
      {
        id: 'fte',
        header: t.currentFte,
        enableSorting: false,
        cell: ({ row }) => (
          <span
            className={row.original.currentFte > 1 ? 'font-medium text-destructive' : undefined}
          >
            {row.original.currentFte || uk.common.empty}
          </span>
        ),
        meta: { className: 'text-right' },
      },
      {
        id: 'phone',
        header: t.phone,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.phoneE164 ? formatPhone(row.original.phoneE164) : row.original.phoneRaw,
        meta: { className: 'whitespace-nowrap' },
      },
      {
        id: 'hiredAt',
        header: t.hiredAt,
        cell: ({ row }) => formatDate(row.original.hiredAt),
      },
      {
        id: 'status',
        header: t.status,
        cell: ({ row }) => <EmployeeStatusBadge status={row.original.status} />,
      },
    ],
    [t],
  );

  return (
    <>
      <PageHeader
        title={t.employees}
        actions={
          canWrite && (
            <Button onClick={() => setSearch({ new: true })}>
              <PlusIcon /> {t.newEmployee}
            </Button>
          )
        }
      />
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <SearchInput
          value={search.q ?? ''}
          placeholder={t.searchPlaceholder}
          onChange={(q) => setSearch({ q: q || undefined, page: undefined })}
        />
        <Select
          value={status}
          onValueChange={(v) =>
            setSearch({
              status: v === 'active' ? undefined : (v as EmployeeStatus),
              page: undefined,
            })
          }
        >
          <SelectTrigger className="w-40" aria-label={t.status}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EMPLOYEE_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {EMPLOYEE_STATUS_LABELS[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Combobox
          className="w-44"
          value={search.project}
          onChange={(v) => setSearch({ project: v ?? undefined, page: undefined })}
          options={(registry.data?.projects ?? []).map((p) => ({
            value: p.id,
            label: p.code,
            hint: p.name,
          }))}
          placeholder={uk.registry.project}
          allowClear
        />
        <Combobox
          className="w-44"
          value={search.center}
          onChange={(v) => setSearch({ center: v ?? undefined, page: undefined })}
          options={(registry.data?.centers ?? []).map((c) => ({ value: c.id, label: c.name }))}
          placeholder={uk.registry.center}
          allowClear
        />
        <Select
          value={search.kind ?? 'all'}
          onValueChange={(v) =>
            setSearch({ kind: v === 'all' ? undefined : (v as ContractKind), page: undefined })
          }
        >
          <SelectTrigger className="w-48" aria-label={uk.registry.contractKind}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {uk.registry.contractKind}: {uk.common.all.toLowerCase()}
            </SelectItem>
            {CONTRACT_KINDS.map((k) => (
              <SelectItem key={k} value={k}>
                {CONTRACT_KIND_LABELS[k]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <DataTable
        columns={columns}
        data={list.data?.items}
        total={list.data?.total ?? 0}
        state={state}
        onStateChange={(next) => setSearch(tableStateToSearch(next))}
        isLoading={list.isPending}
        getRowId={(r) => r.id}
        onRowClick={(r) =>
          void navigate({ to: '/employees/$employeeId', params: { employeeId: r.id } })
        }
        empty={<EmptyState icon={UserRoundIcon} title={t.empty} hint={t.emptyHint} />}
      />
      <EmployeeCreateSheet
        open={Boolean(search.new)}
        onOpenChange={(o) => setSearch({ new: o || undefined })}
      />
    </>
  );
}
